import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertTriangle, ChevronDown, ChevronRight, ChevronUp, Edit, Gift, Image, Loader2, Plus, Trash2, Upload, X } from 'lucide-react';
import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';

const CategoriesEditor = () => {
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
    const [newCategoryName, setNewCategoryName] = useState({ de: '', en: '' });
    const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const utils = trpc.useUtils();
    const { data: categoriesData, isLoading } = trpc.bookingFunnelCMS.getCategories.useQuery();
    const { data: bookableItemsData } = trpc.bookingFunnelCMS.getBookableItems.useQuery();

    const categories = categoriesData?.data || [];
    const bookableItems = bookableItemsData?.data || [];
    const editingCategory = categories.find((c) => c.id === editingCategoryId) ||
        categories.flatMap((c) => c.children || []).find((c) => c.id === editingCategoryId);

    const createMutation = trpc.bookingFunnelCMS.createCategory.useMutation({
        onSuccess: () => {
            toast.success('Kategorie erstellt');
            utils.bookingFunnelCMS.getCategories.invalidate();
            setIsCreateDialogOpen(false);
            setNewCategoryName({ de: '', en: '' });
            setSelectedParentId(null);
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen', { description: error.message });
        },
    });

    const updateMutation = trpc.bookingFunnelCMS.updateCategory.useMutation({
        onSuccess: () => {
            toast.success('Kategorie aktualisiert');
            utils.bookingFunnelCMS.getCategories.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    const deleteMutation = trpc.bookingFunnelCMS.deleteCategory.useMutation({
        onSuccess: () => {
            toast.success('Kategorie gelöscht');
            utils.bookingFunnelCMS.getCategories.invalidate();
            setEditingCategoryId(null);
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen', { description: error.message });
        },
    });

    const uploadImageMutation = trpc.bookingFunnelCMS.uploadCategoryImage.useMutation({
        onSuccess: () => {
            toast.success('Bild hochgeladen');
            utils.bookingFunnelCMS.getCategories.invalidate();
            setIsUploading(false);
        },
        onError: (error) => {
            toast.error('Fehler beim Hochladen', { description: error.message });
            setIsUploading(false);
        },
    });

    const setBookableItemsMutation = trpc.bookingFunnelCMS.setCategoryBookableItems.useMutation({
        onSuccess: () => {
            toast.success('Leistungen aktualisiert');
            utils.bookingFunnelCMS.getCategories.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler', { description: error.message });
        },
    });

    const reorderMutation = trpc.bookingFunnelCMS.reorderCategories.useMutation({
        onSuccess: () => {
            utils.bookingFunnelCMS.getCategories.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Sortieren', { description: error.message });
        },
    });

    const handleMoveCategory = (categoryId: string, direction: 'up' | 'down', parentId: string | null) => {
        // Get the list of categories at the same level
        const categoryList = parentId 
            ? categories.find((c) => c.id === parentId)?.children || []
            : categories;
        
        const currentIndex = categoryList.findIndex((c) => c.id === categoryId);
        if (currentIndex === -1) return;

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= categoryList.length) return;

        // Create new order array
        const newOrder = categoryList.map((cat, idx) => {
            if (idx === currentIndex) return { id: categoryList[newIndex].id, order: currentIndex };
            if (idx === newIndex) return { id: categoryList[currentIndex].id, order: newIndex };
            return { id: cat.id, order: idx };
        });

        reorderMutation.mutate({ categoryOrders: newOrder });
    };

    const handleCreate = () => {
        if (!newCategoryName.de || !newCategoryName.en) {
            toast.error('Bitte geben Sie einen Namen ein');
            return;
        }
        createMutation.mutate({
            nameDe: newCategoryName.de,
            nameEn: newCategoryName.en,
            parentId: selectedParentId ?? undefined,
        });
    };

    const handleDelete = (id: string) => {
        if (confirm('Möchten Sie diese Kategorie wirklich löschen? Alle Unterkategorien werden ebenfalls gelöscht.')) {
            deleteMutation.mutate({ id });
        }
    };

    const handleImageUpload = async (file: File, categoryId: string) => {
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Bild ist zu groß (max. 10MB)');
            return;
        }

        setIsUploading(true);
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result as string;
            uploadImageMutation.mutate({
                categoryId,
                imageBase64: base64,
            });
        };
        reader.readAsDataURL(file);
    };

    const getOrderedBookableItemIds = (category: typeof editingCategory): string[] => {
        return category?.bookableItems?.map((bi) => bi.bookableItem.id) || [];
    };

    const handleAddBookableItem = (categoryId: string, currentItems: string[], itemId: string) => {
        setBookableItemsMutation.mutate({
            categoryId,
            bookableItemIds: [...currentItems, itemId],
        });
    };

    const handleRemoveBookableItem = (categoryId: string, currentItems: string[], itemId: string) => {
        setBookableItemsMutation.mutate({
            categoryId,
            bookableItemIds: currentItems.filter((id) => id !== itemId),
        });
    };

    const handleReorderBookableItem = (categoryId: string, currentItems: string[], fromIndex: number, toIndex: number) => {
        if (toIndex < 0 || toIndex >= currentItems.length) return;
        const newItems = [...currentItems];
        const [moved] = newItems.splice(fromIndex, 1);
        newItems.splice(toIndex, 0, moved);
        setBookableItemsMutation.mutate({
            categoryId,
            bookableItemIds: newItems,
        });
    };

    // Helper to check if a category will display services in the booking funnel
    // A category won't display if it has no bookable items OR if it's a parent with no children that have items
    const getCategoryWarningInfo = (category: typeof categories[0]) => {
        const hasDirectItems = category.bookableItems && category.bookableItems.length > 0;
        const hasChildren = category.children && category.children.length > 0;
        
        if (hasChildren) {
            // Check if any children have items
            const childrenWithItems = category.children.filter((child) => 
                child.bookableItems && child.bookableItems.length > 0
            );
            if (childrenWithItems.length === 0 && !hasDirectItems) {
                return {
                    hasWarning: true,
                    message: 'Diese Kategorie und ihre Unterkategorien haben keine zugewiesenen Leistungen und werden im Buchungs-Funnel nicht angezeigt.',
                };
            }
        } else {
            // Leaf category
            if (!hasDirectItems) {
                return {
                    hasWarning: true,
                    message: 'Diese Kategorie hat keine zugewiesenen Leistungen und wird im Buchungs-Funnel nicht angezeigt.',
                };
            }
        }
        
        return { hasWarning: false, message: '' };
    };

    // Get warning info for the editing category
    const editingCategoryWarning = editingCategory ? 
        getCategoryWarningInfo(editingCategory as typeof categories[0]) : null;

    if (isLoading) {
        return (
            <div className='space-y-4'>
                <Skeleton className='h-10 w-48' />
                <Skeleton className='h-64 w-full' />
            </div>
        );
    }

    return (
        <div className='space-y-6'>
            <div className='flex justify-between items-center'>
                <p className='text-sm text-muted-foreground'>
                    Kategorien für den "Nach Kategorie durchsuchen" Pfad. Unterstützt 1 Ebene Verschachtelung.
                </p>
                <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                    <DialogTrigger asChild>
                        <Button>
                            <Plus className='mr-2 h-4 w-4' />
                            Neue Kategorie
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Neue Kategorie erstellen</DialogTitle>
                            <DialogDescription>
                                Erstellen Sie eine neue Kategorie für den Buchungs-Funnel.
                            </DialogDescription>
                        </DialogHeader>
                        <div className='space-y-4 py-4'>
                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-2'>
                                    <Label htmlFor='catNameDe'>Name (Deutsch)</Label>
                                    <Input
                                        id='catNameDe'
                                        value={newCategoryName.de}
                                        onChange={(e) => setNewCategoryName({ ...newCategoryName, de: e.target.value })}
                                    />
                                </div>
                                <div className='space-y-2'>
                                    <Label htmlFor='catNameEn'>Name (Englisch)</Label>
                                    <Input
                                        id='catNameEn'
                                        value={newCategoryName.en}
                                        onChange={(e) => setNewCategoryName({ ...newCategoryName, en: e.target.value })}
                                    />
                                </div>
                            </div>
                            {categories.length > 0 && (
                                <div className='space-y-2'>
                                    <Label>Übergeordnete Kategorie (optional)</Label>
                                    <select
                                        className='w-full p-2 border rounded-md'
                                        value={selectedParentId || ''}
                                        onChange={(e) => setSelectedParentId(e.target.value || null)}
                                    >
                                        <option value=''>Keine (Hauptkategorie)</option>
                                        {categories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>
                                                {cat.nameDe}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>
                        <DialogFooter>
                            <Button variant='outline' onClick={() => setIsCreateDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button onClick={handleCreate} disabled={createMutation.isPending}>
                                {createMutation.isPending && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                                Erstellen
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            {categories.length === 0 ? (
                <Card>
                    <CardContent className='py-12 text-center text-muted-foreground'>
                        Noch keine Kategorien erstellt. Klicken Sie auf "Neue Kategorie" um zu beginnen.
                    </CardContent>
                </Card>
            ) : (
                <div className='space-y-2'>
                    {categories.map((category, categoryIndex) => (
                        <Card key={category.id}>
                            <CardContent className='p-4'>
                                <div className='flex items-center justify-between'>
                                    <div className='flex items-center gap-4'>
                                        {/* Order controls */}
                                        <div className='flex flex-col'>
                                            <Button
                                                variant='ghost'
                                                size='icon'
                                                className='h-6 w-6'
                                                onClick={() => handleMoveCategory(category.id, 'up', null)}
                                                disabled={categoryIndex === 0 || categories.length <= 1 || reorderMutation.isPending}
                                            >
                                                <ChevronUp className='h-4 w-4' />
                                            </Button>
                                            <Button
                                                variant='ghost'
                                                size='icon'
                                                className='h-6 w-6'
                                                onClick={() => handleMoveCategory(category.id, 'down', null)}
                                                disabled={categoryIndex === categories.length - 1 || categories.length <= 1 || reorderMutation.isPending}
                                            >
                                                <ChevronDown className='h-4 w-4' />
                                            </Button>
                                        </div>
                                        {category.imageUrl ? (
                                            <img
                                                src={category.imageUrl}
                                                alt={category.nameDe}
                                                className='w-12 h-12 object-cover rounded'
                                            />
                                        ) : (
                                            <div className='w-12 h-12 bg-muted rounded flex items-center justify-center'>
                                                <Image className='h-6 w-6 text-muted-foreground' />
                                            </div>
                                        )}
                                        <div>
                                            <div className='flex items-center gap-2'>
                                                <span className='font-medium'>{category.nameDe}</span>
                                                <span className='text-muted-foreground'>/ {category.nameEn}</span>
                                                {getCategoryWarningInfo(category).hasWarning && (
                                                    <TooltipProvider>
                                                        <Tooltip>
                                                            <TooltipTrigger>
                                                                <AlertTriangle className='h-4 w-4 text-amber-500' />
                                                            </TooltipTrigger>
                                                            <TooltipContent>
                                                                <p className='max-w-xs'>{getCategoryWarningInfo(category).message}</p>
                                                            </TooltipContent>
                                                        </Tooltip>
                                                    </TooltipProvider>
                                                )}
                                            </div>
                                            <div className='flex gap-2 mt-1'>
                                                {category.children && category.children.length > 0 && (
                                                    <Badge variant='outline'>
                                                        {category.children.length} Unterkategorien
                                                    </Badge>
                                                )}
                                                {category.bookableItems && category.bookableItems.length > 0 && (
                                                    <Badge variant='secondary'>
                                                        {category.bookableItems.length} Leistungen
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='flex gap-2'>
                                        <Button
                                            variant='ghost'
                                            size='icon'
                                            onClick={() => setEditingCategoryId(category.id)}
                                        >
                                            <Edit className='h-4 w-4' />
                                        </Button>
                                        <Button
                                            variant='ghost'
                                            size='icon'
                                            onClick={() => handleDelete(category.id)}
                                            disabled={deleteMutation.isPending}
                                        >
                                            <Trash2 className='h-4 w-4 text-destructive' />
                                        </Button>
                                    </div>
                                </div>

                                {/* Children */}
                                {category.children && category.children.length > 0 && (
                                    <div className='mt-3 ml-16 space-y-2'>
                                        {category.children.map((child, childIndex) => (
                                            <div
                                                key={child.id}
                                                className='flex items-center justify-between p-2 bg-muted/50 rounded'
                                            >
                                                <div className='flex items-center gap-3'>
                                                    {/* Order controls for children */}
                                                    <div className='flex flex-col'>
                                                        <Button
                                                            variant='ghost'
                                                            size='icon'
                                                            className='h-5 w-5'
                                                            onClick={() => handleMoveCategory(child.id, 'up', category.id)}
                                                            disabled={childIndex === 0 || (category.children?.length || 0) <= 1 || reorderMutation.isPending}
                                                        >
                                                            <ChevronUp className='h-3 w-3' />
                                                        </Button>
                                                        <Button
                                                            variant='ghost'
                                                            size='icon'
                                                            className='h-5 w-5'
                                                            onClick={() => handleMoveCategory(child.id, 'down', category.id)}
                                                            disabled={childIndex === (category.children?.length || 0) - 1 || (category.children?.length || 0) <= 1 || reorderMutation.isPending}
                                                        >
                                                            <ChevronDown className='h-3 w-3' />
                                                        </Button>
                                                    </div>
                                                    <ChevronRight className='h-4 w-4 text-muted-foreground' />
                                                    {child.imageUrl ? (
                                                        <img
                                                            src={child.imageUrl}
                                                            alt={child.nameDe}
                                                            className='w-8 h-8 object-cover rounded'
                                                        />
                                                    ) : (
                                                        <div className='w-8 h-8 bg-muted rounded flex items-center justify-center'>
                                                            <Image className='h-4 w-4 text-muted-foreground' />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span>{child.nameDe}</span>
                                                        <span className='text-muted-foreground ml-2'>/ {child.nameEn}</span>
                                                    </div>
                                                    {child.bookableItems && child.bookableItems.length > 0 && (
                                                        <Badge variant='secondary' className='ml-2'>
                                                            {child.bookableItems.length} Leistungen
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className='flex gap-2'>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        onClick={() => setEditingCategoryId(child.id)}
                                                    >
                                                        <Edit className='h-4 w-4' />
                                                    </Button>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        onClick={() => handleDelete(child.id)}
                                                        disabled={deleteMutation.isPending}
                                                    >
                                                        <Trash2 className='h-4 w-4 text-destructive' />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Edit Dialog */}
            <Dialog open={editingCategoryId !== null} onOpenChange={(open) => !open && setEditingCategoryId(null)}>
                <DialogContent className='max-w-3xl max-h-[80vh] overflow-y-auto'>
                    <DialogHeader>
                        <DialogTitle>Kategorie bearbeiten</DialogTitle>
                        <DialogDescription>
                            Bearbeiten Sie die Kategorie-Details und weisen Sie Leistungen zu.
                        </DialogDescription>
                    </DialogHeader>

                    {editingCategory && (
                        <div className='space-y-6'>
                            {/* Warning banner */}
                            {editingCategoryWarning?.hasWarning && (
                                <div className='p-4 bg-amber-50 border border-amber-200 rounded-lg'>
                                    <div className='flex items-start gap-3'>
                                        <AlertTriangle className='h-5 w-5 text-amber-500 shrink-0 mt-0.5' />
                                        <div>
                                            <p className='font-medium text-amber-800'>Konfigurationsproblem</p>
                                            <p className='text-sm text-amber-700 mt-1'>
                                                {editingCategoryWarning.message}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Basic Info */}
                            <div className='grid grid-cols-2 gap-4'>
                                <div className='space-y-2'>
                                    <Label>Name (Deutsch)</Label>
                                    <Input
                                        value={editingCategory.nameDe}
                                        onChange={(e) =>
                                            updateMutation.mutate({
                                                id: editingCategory.id,
                                                nameDe: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                                <div className='space-y-2'>
                                    <Label>Name (Englisch)</Label>
                                    <Input
                                        value={editingCategory.nameEn}
                                        onChange={(e) =>
                                            updateMutation.mutate({
                                                id: editingCategory.id,
                                                nameEn: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </div>

                            <div className='flex items-center gap-2'>
                                <Switch
                                    checked={editingCategory.isVisible}
                                    onCheckedChange={(checked) =>
                                        updateMutation.mutate({
                                            id: editingCategory.id,
                                            isVisible: checked,
                                        })
                                    }
                                />
                                <Label>Sichtbar im Buchungs-Funnel</Label>
                            </div>

                            <Separator />

                            {/* Image */}
                            <div className='space-y-4'>
                                <h4 className='font-medium'>Kategorie-Bild</h4>
                                <div className='flex items-center gap-4'>
                                    {editingCategory.imageUrl ? (
                                        <img
                                            src={editingCategory.imageUrl}
                                            alt={editingCategory.nameDe}
                                            className='w-24 h-24 object-cover rounded-lg border'
                                        />
                                    ) : (
                                        <div className='w-24 h-24 bg-muted rounded-lg flex items-center justify-center border-2 border-dashed'>
                                            <Image className='h-8 w-8 text-muted-foreground' />
                                        </div>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type='file'
                                        accept='image/*'
                                        className='hidden'
                                        onChange={(e) => {
                                            const file = e.target.files?.[0];
                                            if (file && editingCategoryId) {
                                                handleImageUpload(file, editingCategoryId);
                                            }
                                            e.target.value = '';
                                        }}
                                    />
                                    <Button
                                        variant='outline'
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={isUploading}
                                    >
                                        {isUploading ? (
                                            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                                        ) : (
                                            <Upload className='mr-2 h-4 w-4' />
                                        )}
                                        Bild hochladen
                                    </Button>
                                </div>
                                <p className='text-sm text-muted-foreground'>
                                    Das Bild wird auf max. 500px skaliert.
                                </p>
                            </div>

                            <Separator />

                            {/* Bookable Items Assignment */}
                            {(() => {
                                const orderedItemIds = getOrderedBookableItemIds(editingCategory);
                                const unassignedItems = bookableItems.filter((item) => !orderedItemIds.includes(item.id));

                                return (
                                    <div className='space-y-4'>
                                        <h4 className='font-medium'>Zugewiesene Leistungen</h4>
                                        <p className='text-sm text-muted-foreground'>
                                            Leistungen werden im Buchungs-Funnel in der hier festgelegten Reihenfolge angezeigt.
                                        </p>

                                        {/* Assigned items - ordered */}
                                        <div className='space-y-1 max-h-48 overflow-y-auto border rounded-lg p-2'>
                                            {orderedItemIds.length === 0 ? (
                                                <p className='text-center text-sm text-muted-foreground py-4'>
                                                    Keine Leistungen zugewiesen.
                                                </p>
                                            ) : orderedItemIds.map((itemId, index) => {
                                                const item = bookableItems.find((bi) => bi.id === itemId);
                                                if (!item) return null;
                                                const isBundle = item.type === 'BUNDLE';
                                                return (
                                                    <div key={itemId} className='flex items-center gap-2 p-2 rounded bg-muted/50'>
                                                        <span className='text-xs text-muted-foreground w-5 text-right'>{index + 1}.</span>
                                                        <span className='text-sm flex-1 flex items-center gap-1.5'>
                                                            {isBundle && <Gift className='h-3 w-3 text-purple-600' />}
                                                            {item.nameDe}
                                                            {isBundle && (
                                                                <Badge variant='secondary' className='text-[10px] px-1 py-0 bg-purple-100 text-purple-700'>
                                                                    Bundle
                                                                </Badge>
                                                            )}
                                                        </span>
                                                        <div className='flex items-center gap-0.5'>
                                                            <Button
                                                                variant='ghost'
                                                                size='icon'
                                                                className='h-6 w-6'
                                                                onClick={() => handleReorderBookableItem(editingCategory.id, orderedItemIds, index, index - 1)}
                                                                disabled={index === 0 || setBookableItemsMutation.isPending}
                                                            >
                                                                <ChevronUp className='h-3 w-3' />
                                                            </Button>
                                                            <Button
                                                                variant='ghost'
                                                                size='icon'
                                                                className='h-6 w-6'
                                                                onClick={() => handleReorderBookableItem(editingCategory.id, orderedItemIds, index, index + 1)}
                                                                disabled={index === orderedItemIds.length - 1 || setBookableItemsMutation.isPending}
                                                            >
                                                                <ChevronDown className='h-3 w-3' />
                                                            </Button>
                                                            <Button
                                                                variant='ghost'
                                                                size='icon'
                                                                className='h-6 w-6'
                                                                onClick={() => handleRemoveBookableItem(editingCategory.id, orderedItemIds, itemId)}
                                                                disabled={setBookableItemsMutation.isPending}
                                                            >
                                                                <X className='h-3 w-3' />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Unassigned items - add to end */}
                                        {unassignedItems.length > 0 && (
                                            <>
                                                <h4 className='font-medium text-sm text-muted-foreground'>Verfügbare Leistungen</h4>
                                                <div className='grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2'>
                                                    {unassignedItems.map((item) => {
                                                        const isBundle = item.type === 'BUNDLE';
                                                        return (
                                                            <div
                                                                key={item.id}
                                                                className='flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-muted'
                                                                onClick={() => handleAddBookableItem(editingCategory.id, orderedItemIds, item.id)}
                                                            >
                                                                <Plus className='h-3 w-3 text-muted-foreground' />
                                                                <span className='text-sm flex items-center gap-1.5'>
                                                                    {isBundle && <Gift className='h-3 w-3 text-purple-600' />}
                                                                    {item.nameDe}
                                                                </span>
                                                                {isBundle && (
                                                                    <Badge variant='secondary' className='text-[10px] px-1 py-0 bg-purple-100 text-purple-700'>
                                                                        Bundle
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant='outline' onClick={() => setEditingCategoryId(null)}>
                            Schließen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CategoriesEditor;
